/* MĚNA TENANTA — jediný zdroj pravdy o tom, v čem jsou PENÍZE.
 *
 * PRAVIDLO (tvrdé, celý repo):
 *   Jazyk  = v čem jsou NAPSANÉ TEXTY.
 *   Měna   = v čem jsou PENÍZE.
 *   Nejsou to dvě jména pro jednu věc a nikdy se z jednoho neodvozuje druhé.
 *
 * PROČ TENHLE SOUBOR VZNIKL (nález 2026-08):
 * `LanguageContext` měl tabulku CURRENCY_BY_LANG, kde `sk` neslo `rate: 25`.
 * Český poradce, který si appku přepnul do slovenštiny (protože seděl se
 * slovenským klientem), viděl VŠECHNY částky vydělené 25 a přemalované na eura:
 * plán na 10 000 000 Kč se tvářil jako 400 000 €. V databázi přitom zůstaly
 * koruny. Kurz 25 byl v kódu roky, nikdo ho neaktualizoval a nikde nebyl vidět.
 *
 * CO PLATÍ TEĎ:
 *   1. Měna se bere ze ZEMĚ TENANTA (poradce/organizace): CZ → CZK, SK → EUR.
 *      Země přichází z `/auth/me` (`country_code`, `base_currency`).
 *      Nastavuje si ji majitel účtu v Nastavení → Základní údaje → Země.
 *   2. ŽÁDNÝ PŘEPOČET SE NEDĚLÁ. V repu není zdroj kurzu, takže se ani
 *      nepředstírá, že ho známe. Částka uložená v korunách se ukáže v korunách.
 *      Tenant v eurech zadává a vidí eura. Radši žádný přepočet než přepočet
 *      podle tři roky starého čísla schovaného v kódu.
 *      (Cizoměnové ŘÁDKY analýzy 2.0 jsou jiný příběh — tam kurz zadává poradce
 *      ručně i s datem a je vidět, viz `lib/analyzaV2/meny.js`.)
 *   3. Přepnutí jazyka smí změnit texty a formát čísla (oddělovače), nikdy
 *      hodnotu ani měnu.
 *
 * ⚠️ TŘI KOPIE MAPY ZEMĚ→MĚNA. Když přibude země, musí přibýt na VŠECH třech:
 *   1. `backend/routes/auth.py` (MENA_ZEME)      — hlídá backend/tests/test_organizace_mena.py
 *   2. tenhle soubor                             — hlídá contexts/__tests__/menaZTenantu.test.js
 *   3. `lib/analyzaV2/meny.js` (MENA_ZEME)       — základní měna analýzy 2.0
 * Bod 3 se dlouho nehlídal. Kdyby se rozešel, tenant v nové zemi by v analýze
 * dostal cizí měnu jako „základní" a majetek by se mu sečetl na 0 — přesně to,
 * co popisuje `lib/analyzaV2/__tests__/zakladniMena.test.js`. Shodu obou
 * frontendových map dnes zamyká menaZTenantu.test.js (sekce 8).
 */

/** Země tenanta → měna. Rozšiřuj SPOLU s backendem (routes/auth.py). */
export const MENA_ZEME = {
  CZ: { code: "CZK", symbol: "Kč" },
  SK: { code: "EUR", symbol: "€" },
};

/** Když země není známá. Není to „správná" hodnota, jen nejmenší překvapení. */
export const VYCHOZI_ZEME = "CZ";

/** Země, které umí backend uložit. Cokoli mimo → výchozí. */
export const POVOLENE_ZEME = Object.keys(MENA_ZEME);

/** Data uložená BEZ razítka měny pocházejí z doby před tímhle modulem.
 *  Aplikace tehdy ukládala koruny (viz historie výše), takže legacy = CZK.
 *  Nikdy to nedosazuj potichu — vždy k tomu přidej `legacy: true`, ať UI může
 *  přiznat, že měna je odvozená, ne zapsaná. */
export const MENA_STARYCH_DAT = "CZK";

/** Normalizace kódu země. Neznámá / prázdná → výchozí. */
export function normZeme(countryCode) {
  const kod = String(countryCode || "").trim().toUpperCase();
  return MENA_ZEME[kod] ? kod : VYCHOZI_ZEME;
}

/** Měna podle kódu země. Vrací vždy objekt, nikdy undefined. */
export function menaZeme(countryCode) {
  return MENA_ZEME[normZeme(countryCode)];
}

/* ===================================================================
   MALÝ STORE — proč ne React context

   `LanguageProvider` je v App.js NAD `AuthProvider`, takže si nemůže sáhnout
   na přihlášeného uživatele hookem. Místo přeskládání celého stromu (a rizika,
   že se rozbije něco úplně jiného) drží zemi tenanta tenhle externí store:
   `AuthProvider` do něj zapíše, co přišlo z `/auth/me`, a `LanguageProvider`
   ho čte přes `useSyncExternalStore`.
=================================================================== */

const KLIC = "bp_tenant_country";

function nactiUlozenou() {
  try {
    return normZeme(window.localStorage.getItem(KLIC));
  } catch {
    // Privátní režim / SSR / zakázané úložiště — výchozí je vždy lepší než pád.
    return VYCHOZI_ZEME;
  }
}

let zeme = nactiUlozenou();
const posluchaci = new Set();

/* VEŘEJNÉ PŘEPSÁNÍ ZEMĚ (nález WV-1, 19. 10. 2026)
 * Veřejná stránka poradce (web, vizitka, rezervace, leadovač, funnel) musí
 * mluvit penězi PORADCE, ne návštěvníka. Klient slovenského poradce dřív viděl
 * Kč, protože se četla země z localStorage jeho prohlížeče (výchozí CZ).
 * Přepsání platí JEN V PAMĚTI a jen dokud je stránka otevřená — do localStorage
 * se nezapisuje, jinak by země cizího poradce přetekla do vlastního účtu
 * návštěvníka (třeba kolegy, který si web otevřel). */
let verejnaZeme = null;

/** Aktuální země tenanta (kód, např. CZ). Snapshot pro useSyncExternalStore. */
export function zemeTenanta() {
  return verejnaZeme || zeme;
}

/** Země ÚČTU bez dočasného přepsání (lib/menaDatStranky.js porovnává měnu
 *  dat s měnou účtu). Snapshot pro useSyncExternalStore. */
export function zemeUctu() {
  return zeme;
}

/** Aktuální měna tenanta — { code, symbol }. */
export function menaTenanta() {
  return menaZeme(zemeTenanta());
}

/**
 * Veřejná stránka nastaví zemi poradce (z backendu, `country_code`).
 * NEPERSISTUJE. Vrací úklidovou funkci, která přepsání zruší.
 */
export function prepisZemiVerejne(countryCode) {
  const nova = normZeme(countryCode);
  const zmena = nova !== zemeTenanta();
  verejnaZeme = nova;
  if (zmena) posluchaci.forEach((cb) => cb());
  return () => {
    if (verejnaZeme !== nova) return; // mezitím ji přepsala jiná stránka
    const pred = zemeTenanta();
    verejnaZeme = null;
    if (pred !== zemeTenanta()) posluchaci.forEach((cb) => cb());
  };
}

/** Přihlášení k odběru změn. Vrací odhlašovací funkci. */
export function odebirejZemi(cb) {
  posluchaci.add(cb);
  return () => posluchaci.delete(cb);
}

/**
 * Zapíše zemi tenanta (volá AuthProvider z `/auth/me` a po přihlášení).
 * Persistuje se do localStorage, aby po F5 nebliklo CZK slovenskému tenantovi
 * dřív, než dojede `/auth/me`.
 */
export function nastavZemiTenanta(countryCode) {
  const nova = normZeme(countryCode);
  if (nova === zeme) return zeme;
  zeme = nova;
  try {
    window.localStorage.setItem(KLIC, nova);
  } catch { /* úložiště nedostupné — v paměti to platí i tak */ }
  posluchaci.forEach((cb) => cb());
  return zeme;
}

/**
 * Odhlášení: zapomeň zemi. Na sdíleném počítači nesmí měna předchozího
 * poradce přetéct do dalšího účtu ještě před tím, než dojede `/auth/me`.
 */
export function zapomenZemiTenanta() {
  try {
    window.localStorage.removeItem(KLIC);
  } catch { /* noop */ }
  if (zeme === VYCHOZI_ZEME) return;
  zeme = VYCHOZI_ZEME;
  posluchaci.forEach((cb) => cb());
}

/* ===================================================================
   RAZÍTKO MĚNY NA ULOŽENÝCH DATECH

   Analýza v1 (`financial_maps.data`) i obchody ukládaly holé číslo bez měny.
   Zpětně tedy nejde poznat, v čem bylo zadané. Od teď se do dat zapisuje
   `base_currency`; co ho nemá, je z doby korun → čteme jako CZK a označíme
   jako odvozené (`legacy`), aby to UI mohlo přiznat místo tichého přeznačení.
=================================================================== */

/**
 * Jakou měnu nesou uložená data?
 * @returns {{ mena: string, legacy: boolean }} legacy = razítko chybělo
 */
export function menaUlozenychDat(data) {
  const ulozena = String(data?.base_currency || "").trim().toUpperCase();
  const znama = Object.values(MENA_ZEME).some((m) => m.code === ulozena);
  if (znama) return { mena: ulozena, legacy: false };
  return { mena: MENA_STARYCH_DAT, legacy: true };
}

/**
 * Orazítkuje data měnou tenanta PŘED uložením. Už zapsanou měnu nepřepisuje —
 * analýza si nese měnu, ve které ji poradce klientovi ukázal, i když se
 * později změní země organizace.
 */
export function orazitkujMenu(data, kodMeny) {
  if (!data || typeof data !== "object") return data;
  if (data.base_currency) return data;
  const mena = String(kodMeny || "").trim().toUpperCase()
    || menaTenanta().code;
  return { ...data, base_currency: mena };
}

/* POZOR na obecný helper typu `menaSeNeshoduje(data, mena)`.
 * Jeden tu byl a nikdo ho nevolal — obě stránky si porovnání dělají samy, a to
 * schválně: obě startují s PRÁZDNÝMI daty, než dojede fetch. Obecný helper by
 * na prázdném objektu vrátil „legacy CZK", takže by slovenskému tenantovi na
 * půl vteřiny bliklo varování o nesouladu měny při každém otevření analýzy.
 *
 * Proto se gate píše flash-safe přímo na stránce:
 *   Analýza 2.0 (AnalyzaV2Page)  — `d.base_currency && d.base_currency !== currency.code`
 *                                   (razítko v datech vždy je, prázdný stav je falsy)
 *   Analýza v1 (FinancniMapaPage) — proti stavu `menaDat`, který se plní až po
 *                                   načtení a startuje na měně tenanta
 * Kdo sem bude takový helper vracet, musí vyřešit i tenhle prázdný stav. */
